import { Footer, IFooterLinkBlock } from '@spinwise/react-component-library/Footer';
import { useDispatch, useSelector } from 'react-redux';
import { IconLibrary } from '../../../libraries/icons.library';
import { IconVariantsLibrary } from '../../../libraries/variants.library';
import {
  IApplicationState,
  ILanguageData,
  OVERLAY_CMS_PAGE_SHOW,
  OVERLAY_LANGUAGE_SELECT_SHOW,
  setLanguage,
} from '@spinwise/spinwise-core';
import styles from './FooterComponent.module.scss';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { POSITIONS, EOverlayContentWidth } from 'Components/shared/Overlay/elements/OverlayFixedBg/types';
import classNames from 'classnames';
import { t } from 'i18next';
import { EPageSlugs, HITZ_COUNTRIES, SOF_COUNTRIES } from '../../../types/globals';
import LiveAgent from 'Lib/zendesk';
import useLicencedTerms from 'Hooks/useLicencedTerms';
import LiveChatWrapper from '../LiveAgentWrapper/LiveAgentWrapper';
import { EComponentVisibility } from '@spinwise/react-component-library/Global';
import { BaseIcon } from '@spinwise/react-component-library/BaseIcon';
import useShowAuthButtons from 'Hooks/useShowAuthButtons';

export interface IFooterComponentProps {
  //header specific props
  languages?: ILanguageData;
  footerBehaviorClass?: string;
}

interface IFooterComponentState {
  showLanguageSelect: boolean;
}

const FooterComponent = (props: IFooterComponentProps, state: IFooterComponentState) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const geo = useSelector((state: IApplicationState) => state.globalReducer.geo);
  const currentLanguage = useSelector((state: IApplicationState) => state.globalReducer?.currentLanguage);
  const {
    data: languagesList,
    loading: languagesLoading,
    success: languagesSuccess,
  } = useSelector((state: IApplicationState) => state.globalReducer.languages);

  const hasFooter = useShowAuthButtons();

  const minAge = geo?.reg_min_age ?? 18;

  useEffect(() => {
    // set language
    if (languagesLoading === false && languagesSuccess) {
      const language = languagesList?.filter((lang: any) => lang.code === router?.locale) as any;
      setLanguage(language[0]?.code, language[0]?.name)(dispatch, {});
    }
  }, [dispatch, router.locale, languagesLoading, languagesSuccess, languagesList]);

  const openLanguagePicker = () => {
    // open popup
    if (languagesList.length > 1) {
      dispatch({
        type: OVERLAY_LANGUAGE_SELECT_SHOW,
        data: true,
      });
    }
  };

  const externalLink = <a target="_blank"></a>;

  const handleModalClick = (slug: string) => {
    dispatch({
      type: OVERLAY_CMS_PAGE_SHOW,
      data: {
        show: true,
        data: {
          slug: slug,
          position: POSITIONS.TOP,
          contentWidth: EOverlayContentWidth.LARGE,
        },
      },
    });
  };

  const termsSlug = useLicencedTerms();

  const legalLinks = [
    {
      text: t('footer.link.terms.conditions'),
      onClickEvent: () => {
        handleModalClick(termsSlug);
      },
    },
  ];

  // Separated licence
  if (SOF_COUNTRIES?.includes(geo?.code2)) {
    legalLinks?.push({
      text: t('footer.link.source.of.funds.guide'),
      onClickEvent: () => {
        handleModalClick(EPageSlugs.SOURCE_OF_FUNDS_GUIDE);
      },
    });
  }

  const linkBlocks: Array<IFooterLinkBlock> = [
    {
      title: t('footer.title.legal'),
      links: legalLinks,
    },
    {
      title: t('footer.title.gaming'),
      links: [
        {
          text: t('footer.link.responsible.gaming'),
          onClickEvent: () => {
            handleModalClick(EPageSlugs.RESPONSIBLE_GAMING);
          },
        },
        {
          text: t('footer.link.privacy.policy'),
          onClickEvent: () => {
            handleModalClick(EPageSlugs.PRIVACY_POLICY);
          },
        },
      ],
    },
    ...(geo?.sportsbook
      ? [
          {
            title: t('footer.title.sportsbook'),
            links: [
              {
                text: t('footer.link.sportbook.betting.guide'),
                onClickEvent: () => {
                  handleModalClick(EPageSlugs.SPORTBOOK_BETTING_GUIDE);
                },
              },
              {
                text: t('footer.link.sportsbook.rules'),
                onClickEvent: () => {
                  handleModalClick(EPageSlugs.SPORT_BONUS_TERMS);
                },
              },
            ],
          },
        ]
      : []),
    {
      title: t('footer.title.about.us'),
      links: [
        {
          text: t('footer.link.affiliate.program'),
          link: {
            target: '_blank',
            linkElement: externalLink.type,
            href: t('spinwise.affiliates.url'),
          },
        },
        {
          text: t('footer.link.cookie.policy'),
          onClickEvent: () => {
            handleModalClick(EPageSlugs.COOKIE_POLICY);
          },
        },
      ],
    },
    {
      title: t('footer.title.help'),
      links: [
        {
          text: t('footer.link.contact.us'),
          onClickEvent: () => {
            LiveAgent.openSupportChat();
          },
        },
      ],
    },
  ];

  const license = () => {
    if (typeof window !== 'undefined' && localStorage?.getItem('is-mirror') === 'true') return;

    if (HITZ_COUNTRIES.includes(geo?.code2)) {
      return (
        <div className={styles.license_block}>
          <div className={styles.license_block_left}>
            <div className={styles.license_block_container}>
              <img src="/images/icons/mark.svg" loading="lazy" alt="" className={styles.mark_logo} />
              <p className={styles.license_text_empta}>{t('empta.title')}</p>
            </div>
            <p className={styles.license_text_license}>{t('footer.licence')}</p>
          </div>
          <div className={styles.license_block_right}>
            <div className={styles.footer_icon_contanier}>
              {geo.sportsbook && parseInt(minAge as string) != 18 ? (
                <img src="/images/icons/age-18.svg" loading="lazy" alt="" className={styles.min_age_logo} />
              ) : null}
              {/* 18 or 21 */}
              <img src={`/images/icons/age-${minAge}.svg`} loading="lazy" alt="" className={styles.min_age_logo} />
            </div>
            <p
              className={styles.license_text_advertisement}
              dangerouslySetInnerHTML={{
                __html: t('footer.gambling.advertisement'),
              }}
            ></p>
          </div>
        </div>
      );
    } else {
      return (
          <div className={styles.license_block}>
            <div className={classNames(styles.license_block_auto, 'd-fl jc-c ai-c')}>
              <div
                id="anj-80da342c-93be-4b62-a861-766e5655b0a1"
                data-anj-seal-id="80da342c-93be-4b62-a861-
    766e5655b0a1"
                data-anj-image-size="64"
                data-anj-image-type="basic-small"
              ></div>
            </div>
            <div className={styles.license_block_right}>
              <p className={styles.license_text_license}>{t('footer.licence.anjuano')}</p>
            </div>
          </div>
      );
    }
  };

  useEffect(() => {
    if (geo?.code2 && !HITZ_COUNTRIES.includes(geo?.code2) && !localStorage?.getItem('is-mirror')) {
      //@ts-ignore
      window?.anj_80da342c_93be_4b62_a861_766e5655b0a1?.init?.();
    }
  }, [geo?.code2]);

  return (
    <>
      <div className={classNames({ [styles.no_footer]: !hasFooter })}></div>
      {/* KEEP "display: none" FOR US COUNTRY. PLEASE DO NOT REMOVE DIV WITH ID "anj-80da342c-93be-4b62-a861-766e5655b0a1"
      OTHERWISE WILL BREAK THE anjouan on init */}
      <div
        className={classNames(
          // styles.footer_container,
          props?.footerBehaviorClass,
        )}
        style={!hasFooter ? { display: 'none' } : {}}
      >
        <Footer
          licensesBlock={license()}
          selectedLanguage={{
            language: currentLanguage.name,
            short: currentLanguage?.code?.charAt?.(0).toUpperCase() + currentLanguage?.code?.slice?.(1),
            onClickEvent: () => {
              openLanguagePicker();
            },
          }}
          liveChat={{
            text: t('footer.live.chat.label'),
            onClickEvent: () => {
              LiveAgent.openSupportChat();
            },
          }}
          signature={{
            leftText: {
              text: '',
              link: {
                href: '/',
                linkElement: externalLink.type,
                target: '_blank',
              },
            },
            rightText: t('footer.brand.all.rights.reserved'),
          }}
          links={linkBlocks}
        />
        <LiveChatWrapper
          className={`d-fl ai-c jc-c live_agent_container__icon live-chat-btn ${EComponentVisibility.TableteDesktop}`}
        >
          <BaseIcon
            isActive={true}
            isMasked={true}
            isStatic={true}
            variant={IconVariantsLibrary.White}
            iconName={IconLibrary.Chat}
          />
        </LiveChatWrapper>
      </div>
    </>
  );
};

export default FooterComponent;
